import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { T } from '../utils/theme';
import { fmtBRL, startOfDayCuiaba, endOfDayCuiaba, startOfWeekCuiaba, endOfWeekCuiaba, toCuiabaTimestamp, formatDataCuiaba } from '../utils/dateUtils';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { LargeTitle, SolidCard, SectionHeader, Avatar, KpiTile, Segmented, Sparkline, HeroCard } from '../components/ui';
import { Users, DollarSign, Clock, XCircle, Bell, ArrowUp, ChevronRight, BarChart3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import financeiroService from '../services/financeiroService';

const TZ_CUIABA = 'America/Cuiaba';

const Dashboard = () => {
  const { user } = useAuth();
  const { comandas, agendamentos, barbeiros, configuracoes } = useData();
  const [despesasVencendo, setDespesasVencendo] = useState([]);
  const [periodoSelecionado, setPeriodoSelecionado] = useState('hoje');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');

  useEffect(() => {
    const carregarDespesasVencendo = async () => {
      try {
        const hoje = new Date();
        const amanha = new Date(hoje);
        amanha.setDate(hoje.getDate() + 1);
        const hojeStr = hoje.toISOString().split('T')[0];
        const amanhaStr = amanha.toISOString().split('T')[0];
        const despesas = await financeiroService.listarDespesas({ status: 'aberta', dataInicio: hojeStr, dataFim: amanhaStr });
        const despesasAbertas = despesas.filter(d => d.status === 'aberta');
        setDespesasVencendo([
          ...despesasAbertas.filter(d => d.data === hojeStr).map(d => ({ ...d, tipo: 'hoje' })),
          ...despesasAbertas.filter(d => d.data === amanhaStr).map(d => ({ ...d, tipo: 'amanha' })),
        ]);
      } catch (error) {
        if (error.isAuthError || error.response?.status === 401) return;
      }
    };
    if (user && (user.tipo === 'gestor' || user.tipo === 'caixa')) carregarDespesasVencendo();
  }, [user]);

  const periodoAtual = useMemo(() => {
    const hoje = new Date();
    let inicio = null, fim = null, label = '';
    switch (periodoSelecionado) {
      case 'hoje': inicio = startOfDayCuiaba(hoje); fim = endOfDayCuiaba(hoje); label = 'Hoje'; break;
      case 'ontem': {
        const ontem = new Date(hoje); ontem.setDate(hoje.getDate() - 1);
        inicio = startOfDayCuiaba(ontem); fim = endOfDayCuiaba(ontem); label = 'Ontem'; break;
      }
      case 'semana': inicio = startOfWeekCuiaba(hoje); fim = endOfWeekCuiaba(hoje);
        label = `Semana (${formatDataCuiaba(inicio)} - ${formatDataCuiaba(fim)})`; break;
      case 'mes': {
        const f = new Intl.DateTimeFormat('en-CA', { timeZone: TZ_CUIABA, year: 'numeric', month: '2-digit', day: '2-digit' });
        const [y, m] = f.format(hoje).split('-').map(Number);
        inicio = new Date(Date.UTC(y, m - 1, 1)); fim = new Date(Date.UTC(y, m, 0, 23, 59, 59, 999));
        label = `Este mes`; break;
      }
      case 'personalizado':
        if (!dataInicio || !dataFim) { label = 'Personalizado'; break; }
        inicio = startOfDayCuiaba(dataInicio); fim = endOfDayCuiaba(dataFim);
        label = `${formatDataCuiaba(inicio)} - ${formatDataCuiaba(fim)}`; break;
      default: inicio = startOfDayCuiaba(hoje); fim = endOfDayCuiaba(hoje); label = 'Hoje';
    }
    return { inicio, fim, label };
  }, [periodoSelecionado, dataInicio, dataFim]);

  const filtrarPorPeriodo = useCallback(() => {
    if (!periodoAtual.inicio || !periodoAtual.fim) return comandas;
    const inicioMs = periodoAtual.inicio.getTime(), fimMs = periodoAtual.fim.getTime();
    return comandas.filter(c => { const ms = toCuiabaTimestamp(c.data); return ms >= inicioMs && ms <= fimMs; });
  }, [comandas, periodoAtual]);

  const comandasFiltradas = user?.tipo === 'barbeiro'
    ? filtrarPorPeriodo().filter(c => c.barbeiroId === user.barbeiroId)
    : filtrarPorPeriodo();

  const agendamentosFiltrados = user?.tipo === 'barbeiro'
    ? agendamentos.filter(a => a.barbeiroId === user.barbeiroId) : agendamentos;

  const comandasPagas = comandasFiltradas.filter(c => c.status === 'pago');
  const faturamentoTotal = comandasPagas.reduce((s, c) => s + c.total, 0);
  const atendimentosTotal = comandasPagas.length;
  const ticketMedio = atendimentosTotal > 0 ? faturamentoTotal / atendimentosTotal : 0;

  const agendamentosConfirmados = agendamentosFiltrados.filter(a => a.status === 'confirmado').length;
  const agendamentosConcluidos = agendamentosFiltrados.filter(a => a.status === 'concluido').length;
  const totalAgendamentos = agendamentosFiltrados.length;
  const taxaOcupacao = totalAgendamentos > 0 ? ((agendamentosConfirmados + agendamentosConcluidos) / totalAgendamentos * 100).toFixed(0) : 0;

  const cancelados = comandasFiltradas.filter(c => c.status === 'cancelado').length;

  const ultimos7Dias = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(); d.setDate(d.getDate() - i); d.setHours(0, 0, 0, 0);
    const cDia = comandasPagas.filter(c => { const dc = new Date(c.data); dc.setHours(0, 0, 0, 0); return dc.getTime() === d.getTime(); });
    ultimos7Dias.push({ dia: d.toLocaleDateString('pt-BR', { weekday: 'short' }).slice(0, 3), valor: cDia.reduce((s, c) => s + c.total, 0) });
  }
  const sparkData = ultimos7Dias.map(d => d.valor);

  const inicioSemanaMs = startOfWeekCuiaba(new Date()).getTime();
  const fimSemanaMs = endOfWeekCuiaba(new Date()).getTime();
  const comandasSemanaRanking = comandas.filter(c => {
    if (c.status !== 'pago') return false;
    const ms = toCuiabaTimestamp(c.data);
    return ms >= inicioSemanaMs && ms <= fimSemanaMs;
  });

  const rankingBarbeiros = barbeiros.filter(b => b.ativo).map(b => {
    const cb = comandasSemanaRanking.filter(c => c.barbeiroId === b.id);
    return { nome: b.nome, atendimentos: cb.length, faturamento: cb.reduce((s, c) => s + c.total, 0), barbeiroId: b.id, color: null };
  }).sort((a, b) => b.faturamento - a.faturamento || a.nome.localeCompare(b.nome));

  const servicosVendidos = {};
  comandasPagas.forEach(c => { (c.servicos || []).forEach(s => { servicosVendidos[s.nome] = (servicosVendidos[s.nome] || 0) + 1; }); });
  const dadosServicos = Object.entries(servicosVendidos).map(([nome, quantidade]) => ({ nome, quantidade })).sort((a, b) => b.quantidade - a.quantidade).slice(0, 5);

  const rankColors = [T.warning, T.label3, '#CD7F32', T.fill2, T.fill2];
  const barberColors = [T.warning, T.indigo, T.pink, T.success, T.purple];

  const hoje = new Date();
  const diaSemana = hoje.toLocaleDateString('pt-BR', { weekday: 'long' });
  const diaFormatado = hoje.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' });

  return (
    <div style={{ padding: '0 0 40px' }}>
      <LargeTitle
        title="Inicio"
        subtitle={`Ola, ${user?.nome} — ${diaSemana}, ${diaFormatado}`}
        trailing={
          <button style={{
            border: 'none', cursor: 'pointer', width: 38, height: 38, borderRadius: 19,
            background: T.fill, color: T.label,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}><Bell size={20} /></button>
        }
      />

      {/* Despesas vencendo */}
      {despesasVencendo.length > 0 && (
        <div style={{ padding: '0 20px 8px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {despesasVencendo.filter(d => d.tipo === 'hoje').length > 0 && (
            <div style={{ background: 'rgba(255,59,48,0.1)', borderRadius: 16, padding: '12px 16px', border: `1px solid rgba(255,59,48,0.2)` }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: T.danger, marginBottom: 4 }}>
                {despesasVencendo.filter(d => d.tipo === 'hoje').length} conta(s) vencendo hoje
              </div>
              {despesasVencendo.filter(d => d.tipo === 'hoje').slice(0, 2).map(d => (
                <div key={d.id} style={{ fontSize: 12, color: T.danger, opacity: 0.8 }}>{d.titulo} — {fmtBRL(d.valor)}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Period filter */}
      <div style={{ padding: '6px 20px 0' }}>
        <Segmented
          items={[
            { value: 'hoje', label: 'Hoje' },
            { value: 'semana', label: 'Semana' },
            { value: 'mes', label: 'Mes' },
          ]}
          value={periodoSelecionado}
          onChange={setPeriodoSelecionado}
        />
      </div>

      {/* Hero KPI — Faturamento */}
      <div style={{ padding: '18px 20px 0' }}>
        <SolidCard padding={20} radius={24}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, color: T.label2, letterSpacing: '-0.01em' }}>
                Faturamento {periodoAtual.label.toLowerCase()}
              </div>
              <div className="tnum" style={{ fontSize: 36, fontWeight: 700, color: T.label, letterSpacing: '-0.03em', marginTop: 4 }}>
                {fmtBRL(faturamentoTotal)}
              </div>
            </div>
            {faturamentoTotal > 0 && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '5px 9px', borderRadius: 999,
                background: 'rgba(48,209,88,0.14)', color: T.success, fontSize: 13, fontWeight: 600,
              }}>
                <ArrowUp size={12} color={T.success} strokeWidth={2.4} />
                ativo
              </div>
            )}
          </div>
          {sparkData.some(v => v > 0) && (
            <div style={{ marginTop: 16, marginLeft: -4, marginRight: -4 }}>
              <Sparkline data={sparkData} color={T.accent} height={56} />
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: T.label3, marginTop: 6, padding: '0 2px' }}>
            {ultimos7Dias.map((d, i) => <span key={i}>{d.dia}</span>)}
          </div>
        </SolidCard>
      </div>

      {/* KPI grid */}
      <div className="bx-kpi-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: '12px 20px 0' }}>
        <KpiTile icon={Users} tint={T.accent} label="Atendimentos" value={atendimentosTotal} sub={periodoAtual.label.toLowerCase()} />
        <KpiTile icon={DollarSign} tint={T.success} label="Ticket medio" value={fmtBRL(ticketMedio)} sub="por cliente" />
        <KpiTile icon={Clock} tint={T.warning} label="Taxa ocupacao" value={`${taxaOcupacao}%`} sub="capacidade" />
        <KpiTile icon={XCircle} tint={T.danger} label="Cancelados" value={cancelados} sub={periodoAtual.label.toLowerCase()} />
      </div>

      <div className="bx-cols-2">
        {/* Ranking de barbeiros */}
        <div style={{ padding: '4px 20px 0' }}>
          <SectionHeader title="Ranking de barbeiros" />
          <SolidCard padding={0} radius={20}>
            {rankingBarbeiros.slice(0, 5).map((b, i) => {
              const podeVer = user?.tipo !== 'barbeiro';
              const ehLogado = user?.tipo === 'barbeiro' && user?.barbeiroId === b.barbeiroId;
              const mostrar = podeVer || ehLogado;
              return (
                <div key={b.nome} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                  borderBottom: i < Math.min(rankingBarbeiros.length, 5) - 1 ? `0.5px solid ${T.separator}` : 'none',
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 14,
                    background: i < 3 ? rankColors[i] : T.fill2,
                    color: i < 3 ? '#fff' : T.label2,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: 13, flexShrink: 0,
                  }}>{i + 1}</div>
                  <Avatar name={b.nome} size={36} tint={barberColors[i]} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: T.label, letterSpacing: '-0.01em' }}>{b.nome}</div>
                    <div style={{ fontSize: 12, color: T.label2, marginTop: 1 }}>{b.atendimentos} atendimentos</div>
                  </div>
                  <div className="tnum" style={{ fontSize: 15, fontWeight: 600, color: T.label, letterSpacing: '-0.01em' }}>
                    {mostrar ? fmtBRL(b.faturamento) : '***'}
                  </div>
                </div>
              );
            })}
            {rankingBarbeiros.length === 0 && (
              <div style={{ padding: 20, textAlign: 'center', color: T.label3, fontSize: 14 }}>Nenhum barbeiro com atendimentos</div>
            )}
          </SolidCard>
        </div>

        {/* Servicos mais vendidos */}
        {dadosServicos.length > 0 && (
          <div style={{ padding: '4px 20px 0' }}>
            <SectionHeader title="Servicos mais vendidos" />
            <SolidCard padding={16} radius={20}>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={dadosServicos}>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.separator} />
                  <XAxis dataKey="nome" stroke={T.label3} tick={{ fontSize: 11 }} />
                  <YAxis stroke={T.label3} tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: `1px solid ${T.separator}`, boxShadow: T.shadow }} />
                  <Bar dataKey="quantidade" radius={[6, 6, 0, 0]} fill="url(#barGrad)" />
                  <defs>
                    <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={T.accent} />
                      <stop offset="100%" stopColor={T.indigo} />
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
            </SolidCard>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
